import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Eye, EyeOff, Shield, Smartphone, Key } from 'lucide-react';

export default function ProfilePage() {
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [twoFAData, setTwoFAData] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [enabling2FA, setEnabling2FA] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem('yh_user') || '{}');
    setUser(u);
  }, []);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) { toast.error('Passwörter stimmen nicht überein'); return; }
    if (pwForm.new_password.length < 8) { toast.error('Mindestens 8 Zeichen'); return; }
    setSavingPw(true);
    try {
      await api.post('/auth/change-password', { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success('Passwort geändert');
      setPwForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); } finally { setSavingPw(false); }
  };

  const handleSetup2FA = async () => {
    try {
      const res = await api.post('/auth/2fa/setup/totp');
      setTwoFAData(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  const handleConfirm2FA = async () => {
    if (!otpCode || otpCode.length < 6) { toast.error('Code eingeben'); return; }
    setEnabling2FA(true);
    try {
      await api.post('/auth/2fa/confirm/totp', { secret: twoFAData.secret, code: otpCode });
      toast.success('2FA aktiviert');
      setTwoFAData(null);
      setOtpCode('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Ungültiger Code'); } finally { setEnabling2FA(false); }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-2xl">
        <div className="mb-6">
          <h1 className="font-heading font-black text-2xl">Mein Konto</h1>
          <p className="text-sm text-muted-foreground mt-1">{user?.email}</p>
        </div>

        <Tabs defaultValue="password">
          <TabsList className="rounded-sm mb-6">
            <TabsTrigger value="password" className="rounded-sm"><Key className="w-4 h-4 mr-2" />Passwort</TabsTrigger>
            <TabsTrigger value="2fa" className="rounded-sm"><Smartphone className="w-4 h-4 mr-2" />2-Faktor</TabsTrigger>
          </TabsList>

          {/* Passwort ändern */}
          <TabsContent value="password">
            <div className="bg-card border border-border/60 rounded-sm p-6">
              <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-4">Passwort ändern</h3>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Aktuelles Passwort</Label>
                  <Input type="password" value={pwForm.current_password} onChange={e => setPwForm(f => ({ ...f, current_password: e.target.value }))} className="rounded-sm" autoComplete="current-password" />
                </div>
                <div className="space-y-1.5">
                  <Label>Neues Passwort</Label>
                  <div className="relative">
                    <Input type={showPw ? 'text' : 'password'} value={pwForm.new_password} onChange={e => setPwForm(f => ({ ...f, new_password: e.target.value }))} placeholder="Mindestens 8 Zeichen" className="rounded-sm pr-10" />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowPw(s => !s)}>{showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Passwort bestätigen</Label>
                  <Input type="password" value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} placeholder="Wiederholen" className="rounded-sm" />
                </div>
                <Button type="submit" disabled={savingPw} className="rounded-sm">{savingPw ? 'Speichern...' : 'Passwort speichern'}</Button>
              </form>
            </div>
          </TabsContent>

          {/* 2FA */}
          <TabsContent value="2fa">
            <div className="bg-card border border-border/60 rounded-sm p-6">
              <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-4">Zwei-Faktor-Authentifizierung</h3>
              {!twoFAData ? (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 bg-muted rounded-sm">
                    <Shield className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Konto mit 2FA schützen</p>
                      <p className="text-xs text-muted-foreground mt-1">Verwenden Sie eine Authenticator-App wie Google Authenticator oder Authy.</p>
                    </div>
                  </div>
                  <Button onClick={handleSetup2FA} className="rounded-sm gap-2">
                    <Smartphone className="w-4 h-4" />2FA einrichten
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm">Scannen Sie den QR-Code mit Ihrer Authenticator-App:</p>
                  <div className="flex justify-center">
                    <img src={twoFAData.qr_code} alt="QR Code" className="w-48 h-48 border rounded-sm" />
                  </div>
                  <p className="text-xs text-muted-foreground text-center">Oder geben Sie diesen Code manuell ein: <code className="bg-muted px-1 rounded">{twoFAData.secret}</code></p>
                  <div className="space-y-1.5">
                    <Label>Bestätigungscode (6-stellig)</Label>
                    <Input value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g,'').slice(0,6))} placeholder="123456" className="rounded-sm text-center text-lg tracking-widest" maxLength={6} />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleConfirm2FA} disabled={enabling2FA} className="rounded-sm flex-1">{enabling2FA ? 'Aktivieren...' : '2FA aktivieren'}</Button>
                    <Button variant="outline" onClick={() => setTwoFAData(null)} className="rounded-sm">Abbrechen</Button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
